import React from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw, ExternalLink } from 'lucide-react';
import PaywallGuard from '../PaywallGuard';
import RateAlerts from '../RateAlerts';
import LenderIncentives from '../LenderIncentives';
import RateHistoryChart from '../RateHistoryChart';
import RateLockAlerts from '../RateLockAlerts';

const RatesTab = ({ marketRates, refreshRates, user }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'declining') return <TrendingDown className="h-4 w-4 text-green-400" />;
    if (trend === 'rising') return <TrendingUp className="h-4 w-4 text-red-400" />;
    return <Minus className="h-4 w-4 text-yellow-400" />;
  };
  
  return (
    <div data-testid="rates-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Market Rates & Forecasts</h2>
          <p className="text-muted-foreground text-sm mt-1">Live rates from major Canadian lenders</p>
        </div>
        
        <PaywallGuard feature="rate_refresh" user={user} fallbackMessage="">
          <button
            onClick={refreshRates}
            data-testid="refresh-market-rates-btn"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Rates
          </button>
        </PaywallGuard>
      </div>
      
      {/* Data Freshness Indicator */}
      {marketRates.data_freshness && (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
          marketRates.data_freshness === 'live' 
            ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
            : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
        }`}>
          <span className={`h-2 w-2 rounded-full ${marketRates.data_freshness === 'live' ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`}></span>
          {marketRates.data_freshness === 'live' ? 'Live Data' : 'Cached Data'}
          {marketRates.last_updated && (
            <span className="text-muted-foreground">
              · Updated {new Date(marketRates.last_updated).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}
      
      {/* Economic Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-1">Bank of Canada Rate</p>
          <p className="text-2xl font-bold font-mono">{marketRates.boc_rate}%</p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-1">Prime Rate</p>
          <p className="text-2xl font-bold font-mono">{marketRates.prime_rate || (marketRates.boc_rate + 2.2).toFixed(2)}%</p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-1">Inflation (CPI YoY)</p>
          <p className="text-2xl font-bold font-mono">{marketRates.economic_indicators?.inflation_cpi || 0}%</p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-1">Unemployment Rate</p>
          <p className="text-2xl font-bold font-mono">{marketRates.economic_indicators?.unemployment_rate || 0}%</p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-1">5Y Bond Yield</p>
          <p className="text-2xl font-bold font-mono">{marketRates.economic_indicators?.bond_yield_5y || 0}%</p>
        </div>
      </div>
      
      {/* Rates Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold">Lender</th>
                <th className="text-right px-6 py-3 text-sm font-semibold">3Y Fixed</th>
                <th className="text-right px-6 py-3 text-sm font-semibold">5Y Fixed</th>
                <th className="text-right px-6 py-3 text-sm font-semibold">3Y Variable</th>
                <th className="text-right px-6 py-3 text-sm font-semibold">5Y Variable</th>
                <th className="text-center px-6 py-3 text-sm font-semibold">Trend</th>
                <th className="text-center px-6 py-3 text-sm font-semibold">Source</th>
                <th className="text-right px-6 py-3 text-sm font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {marketRates.rates && marketRates.rates.map((rate, idx) => (
                <tr key={idx} className={`hover:bg-muted/30 transition-colors ${idx === 0 ? 'bg-primary/5' : ''}`}>
                  <td className="px-6 py-4">
                    <span className="font-medium">{rate.lender}</span>
                    {idx === 0 && <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Best Rate</span>}
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{rate.fixed_3y}%</td>
                  <td className="px-6 py-4 text-right font-mono">{rate.fixed_5y}%</td>
                  <td className="px-6 py-4 text-right font-mono">{rate.variable_3y}%</td>
                  <td className="px-6 py-4 text-right font-mono">{rate.variable_5y}%</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-1">
                      {getTrendIcon(rate.trend)}
                      <span className="text-xs capitalize">{rate.trend}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      rate.source === 'RateHub.ca' 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {rate.source || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-muted-foreground">{rate.last_updated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Rate Forecast */}
      <PaywallGuard feature="rate_forecasts" user={user} fallbackMessage="Upgrade to Premium or Pro to access rate forecasts and projections.">
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Rate Forecast</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold mb-3">Short-term Outlook (3-12 months)</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Rates expected to stabilize as inflation moderates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Bank of Canada may hold rates through Q2 2025</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Fixed rates may see slight decreases (0.1-0.2%)</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold mb-3">Long-term Outlook (2-5 years)</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Gradual rate normalization expected by 2026</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>5-year fixed rates may settle around 4.0-4.5%</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Variable rates remain sensitive to policy changes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PaywallGuard>
      
      {/* Data Sources */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-sm font-semibold mb-3">Data Sources</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <a
            href="https://www.bankofcanada.ca/rates/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline"
          >
            Bank of Canada <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://www.ratehub.ca/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline"
          >
            RateHub.ca <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://wowa.ca/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline"
          >
            WOWA.ca <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Disclaimer: Rates shown are indicative and may vary. Always verify with lenders directly.
        </p>
      </div>
      
      {/* Rate History Chart */}
      <PaywallGuard feature="rate_history" user={user} fallbackMessage="Upgrade to Premium or Pro to view historical rate trends.">
        <RateHistoryChart user={user} />
      </PaywallGuard>
      
      {/* Rate Lock Alerts (Pro Feature) */}
      <RateLockAlerts user={user} currentRates={marketRates} />
      
      {/* Rate Alerts Section */}
      <RateAlerts user={user} currentRates={marketRates} />
      
      {/* Lender Incentives Section */}
      <LenderIncentives />
    </div>
  );
};

export default RatesTab;